import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Plus } from 'lucide-react'
import {
  Card, EmptyState, Loading, SearchInput, Select, StatusPill, Th, sortRows, useSort,
} from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { usePos, useSiteNames, useSites, useVendors } from '@/lib/data'
import { can, seesManySites, visibleSites } from '@/lib/domain'
import { ddmmyyyy, rupees } from '@/lib/format'

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Awaiting Stores review' },
  { value: 'REVIEWED', label: 'Awaiting final approval' },
  { value: 'APPROVED', label: 'Approved — ready to send' },
  { value: 'VENDIN', label: 'Sent to vendor' },
  { value: 'VENDACK', label: 'Vendor acknowledged' },
  { value: 'PARTIALLY_RECEIVED', label: 'Partially received' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'RETURNED', label: 'Returned for revision' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'FORCED_CLOSE', label: 'Null and void' },
]

export function PoList() {
  const { profile } = useAuth()
  const role = profile!.role
  const ho = seesManySites(role)
  // A Project Manager picking a colleague's site would see nothing, which
  // looks like a fault rather than a boundary. Offer him only his own.
  const [site, setSite] = useState(ho ? '' : (profile!.site_id ?? ''))
  const [status, setStatus] = useState('')
  const [term, setTerm] = useState('')

  const { data: sites } = useSites()
  const { siteName } = useSiteNames()
  const mySites = useMemo(() => visibleSites(sites, role, profile!.id), [sites, role, profile])
  const { data: vendors } = useVendors()
  const { data, loading, error } = usePos({
    site_id: site || undefined,
    status: status ? [status] : undefined,
  })

  const vendorName = useMemo(
    () => Object.fromEntries(vendors.map((v) => [v.id, v.company_name])), [vendors])

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((p) =>
      p.id.toLowerCase().includes(t) ||
      (vendorName[p.vendor_id] ?? '').toLowerCase().includes(t) ||
      (p.pr_id ?? '').toLowerCase().includes(t))
  }, [data, term, vendorName])

  // Sorting works on the row as displayed, so the vendor column sorts by the
  // vendor's name rather than the code the user never sees.
  const { sort, toggle } = useSort('date_created')
  const sorted = useMemo(
    () => sortRows(rows.map((p) => ({ ...p, vendor_name: vendorName[p.vendor_id] ?? p.vendor_id })), sort, {
      date_created: 'date', delivery_date: 'date', total_amount: 'number',
    }), [rows, sort, vendorName])

  return (
    <div className="space-y-4">
      <Card
        title="Purchase Orders"
        subtitle="Raised by Procurement, reviewed by the Head of Stores, approved by the Procurement Manager"
        actions={can(role, 'createPo') && (
          <Link to="/pos/new" className="btn-primary btn-sm"><Plus className="h-4 w-4" /> New purchase order</Link>
        )}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search PO number, vendor, requisition…" />
          {ho && (
            <Select
              value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
              options={mySites.map((s) => ({ value: s.id, label: s.site_name }))}
            />
          )}
          <Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUSES} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load purchase orders" message={error} /></div>}
        {!loading && !error && rows.length === 0 && (
          <EmptyState
            icon={<FileText className="h-9 w-9" />}
            title="No purchase orders match"
            message="Adjust the filters, or raise a new order from an approved requisition."
          />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>PO number</Th><Th id="vendor_name" sort={sort} onSort={toggle}>Vendor</Th><Th id="site_id" sort={sort} onSort={toggle}>Ship to</Th><Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  <Th id="date_created" sort={sort} onSort={toggle}>Raised</Th><Th id="delivery_date" sort={sort} onSort={toggle}>Delivery by</Th><Th id="pr_id" sort={sort} onSort={toggle}>Against PR</Th>
                  <Th id="gst_treatment" sort={sort} onSort={toggle}>GST</Th><Th id="total_amount" sort={sort} onSort={toggle} className="num" align="right">Total</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/pos/${p.id}`} className="font-mono text-xs font-semibold text-oval-700 hover:underline">
                        {p.id}
                      </Link>
                      {p.is_legacy && <span className="pill-grey ml-1.5">migrated</span>}
                    </td>
                    <td className="max-w-[220px] truncate text-sm">{vendorName[p.vendor_id] ?? p.vendor_id}</td>
                    {/* The name, with the identifier on hover for support. */}
                    <td className="text-xs" title={p.site_id}>{siteName(p.site_id)}</td>
                    <td><StatusPill status={p.status} /></td>
                    <td className="text-xs">{ddmmyyyy(p.date_created)}</td>
                    <td className="text-xs">{ddmmyyyy(p.delivery_date)}</td>
                    <td className="font-mono text-xs text-slate-500">{p.pr_id ?? '—'}</td>
                    <td className="text-xs">
                      {p.gst_treatment === 'NONE' ? <span className="pill-grey">None</span>
                        : p.gst_treatment === 'IGST' ? <span className="pill-blue">IGST</span>
                        : <span className="pill-violet">CGST+SGST</span>}
                    </td>
                    <td className="num text-xs font-semibold">{rupees(p.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
